import type { NodeDisplay } from '@integrate/contracts';
import { useMemo } from 'react';
import {
  computeDedication,
  fmt,
  METRICS,
  METRIC_FULL_FORMAT,
  type MetricKey,
  type SeparationState,
  offeredMetrics,
} from '@integrate/core';
import { MetricToggle } from './MetricToggle.js';
import { StatTile } from '../ui/StatTile.js';

/*
  Dedication: how much of the question has been answered, and where the shared people go.

  Two halves measuring two different cohorts, and the app printed both under the same ◐ glyph with
  nothing saying which the table below was dividing up. COVERAGE is about the numeric data — who has a
  value, how the values spread, what fraction of FTE they account for. ALLOCATION is about the SHARED
  cohort — partially dedicated, or carrying the pre-dedication marker — and which side each of them
  lands on.

  ALL THREE DESTINATIONS, ALWAYS. The app showed two tiles, → Remain and → Carve-out, over a denominator
  that included a third destination it never drew. Ten shared people splitting 4 / 4 / 2 read as
  "40% and 40%", and the missing fifth was one shared person in five being made redundant — the single
  most consequential fact about the cohort.

  ONE SCOPE, NOT TWO. The app put an Among-shared / Share-of-total toggle immediately above four tiles
  that ignored it: clicking it changed the table's → Remain column from 62% to 3% and left a tile
  reading 62% directly underneath. Both denominators are worth having, so both are in the table as their
  own columns — and each has a heading naming what it divides by, rather than one pair of columns whose
  meaning depends on a button somewhere else.

  NOT A LENS: no filters, no stage, no perspective, no view mode. `computeDedication` takes none, and the
  frozen pane already read the full roster — one of the few places it did.
*/

export function DedicationPane({
  state,
  colors,
  metric,
  onMetric,
  levelKey,
  onLevelKey,
}: {
  state: SeparationState;
  /** The card palette, so a bucket has one colour across the whole tool. Never re-chosen here. */
  colors: NodeDisplay['colors']['status'];
  /** Owned by the modal, so a tab switch does not discard it. Shared with the phase bridge, whose
   *  "Cost" is the same raw-salary weight this one uses. */
  metric: MetricKey;
  onMetric(next: MetricKey): void;
  levelKey: string | null;
  onLevelKey(next: string | null): void;
}): JSX.Element {
  const D = useMemo(() => computeDedication(state, { metric, levelKey }), [state, metric, levelKey]);
  const format = METRIC_FULL_FORMAT[metric];
  const pct = (x: number, d: number): string => (d ? `${Math.round((x / d) * 100)}%` : '—');

  const nothingRecorded =
    D.withDedicationN === 0 && D.sharedN === 0 && !state.columnMap.dedication && !state.columnMap.splitHead;
  if (nothingRecorded) {
    return (
      <p className="muted" data-testid="dedication-unmapped">
        Nothing here yet. Map a <strong>Dedication</strong> column in the import mapping — a 0–1 ratio or
        a 0–100% share of each person&apos;s work serving the carve-out — or set it per person in the
        side panel, and this pane will show how the dedicated workforce is distributed and where the
        shared people are being sent.
      </p>
    );
  }

  const maxBin = Math.max(1, ...D.bins);

  return (
    <div data-testid="dedication-pane">
      <h4>Dedication data</h4>
      <div
        className="stat-grid"
        data-testid="dedication-coverage"
        data-with={D.withDedicationN}
        data-employees={D.employeeN}
        data-buildups={D.buildUpN}
        /* The ratio itself, not only its parts: the denominator is the thing the app got wrong. */
        data-coverage={D.coverageOfEmployees}
      >
        <StatTile
          label="With dedication data"
          valueTestId="ded-with"
          value={fmt(D.withDedicationN)}
          /*
            Of the people who COULD have a value. The app divided by the whole population including
            planned hires, which can never carry dedication — so its coverage figure fell every time
            somebody planned another hire, and a fully-measured roster could read 80%.
          */
          sub={`${Math.round(D.coverageOfEmployees * 100)}% of ${fmt(D.employeeN)} existing people`}
        />
        <StatTile
          label="Fully dedicated"
          valueTestId="ded-full"
          value={fmt(D.fullyDedicatedN)}
          sub="all of their work"
        />
        <StatTile
          label="◐ Partially dedicated"
          valueTestId="ded-partial"
          value={fmt(D.partiallyDedicatedN)}
          sub="a measured share, shared with RemainCo"
        />
        <StatTile
          label="Dedicated FTE"
          valueTestId="ded-fte"
          value={fmt(D.dedicatedFte, 1)}
          sub="Σ FTE × dedication, measured people only"
        />
      </div>

      {/*
        What the coverage figure is silent about, named. The app's note disclosed only the legacy
        exclusion — the smaller of the two — and never the people nobody has asked about at all.
      */}
      <p className="muted small" data-testid="dedication-gaps" data-nodata={D.noDataN} data-legacy={D.legacyOnlyN}>
        {D.noDataN > 0 ? (
          <>
            <strong>{fmt(D.noDataN)}</strong> {D.noDataN === 1 ? 'person has' : 'people have'} no
            dedication recorded — {fmt(D.unmeasuredFte, 1)} FTE that the figure above cannot see. That is
            a gap in the data, not a zero: {fmt(D.zeroDedicatedN)}{' '}
            {D.zeroDedicatedN === 1 ? 'person is' : 'people are'} recorded as 0%, which is an answer.
          </>
        ) : (
          <>
            Everybody has a dedication value, so nothing above is a gap standing in for a zero.{' '}
            {fmt(D.zeroDedicatedN)} recorded as 0%.
          </>
        )}
        {D.legacyOnlyN > 0 && (
          <>
            {' '}
            <strong>{fmt(D.legacyOnlyN)}</strong> shared{' '}
            {D.legacyOnlyN === 1 ? 'person carries' : 'people carry'} only the older yes/no marker with no
            number: counted as shared below, absent from the distribution and from Σ FTE × dedication.
          </>
        )}
      </p>

      {D.withDedicationN > 0 && (
        <>
          <h5>Dedication distribution (people per band)</h5>
          <div className="histo" data-testid="dedication-histogram" data-max={maxBin}>
            {D.bins.map((n, i) => (
              <div
                className="bar"
                key={i}
                data-band={i}
                data-n={n}
                style={{ height: `${((n / maxBin) * 100).toFixed(1)}%` }}
                title={`Dedication ${i * 10}–${i * 10 + 10}%: ${fmt(n)} ${n === 1 ? 'person' : 'people'}`}
              >
                <div className="val">{n || ''}</div>
                <div className="lbl">
                  {i * 10}–{i * 10 + 10}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <h4>Shared-cohort allocation</h4>
      {D.sharedN === 0 ? (
        <p className="muted" data-testid="dedication-no-shared">
          {/*
            Which of the two, said plainly. "Everybody with a value is fully or not dedicated" is
            vacuously true when nobody has a value, and the app printed exactly that over a roster
            nobody had measured — the state you are in immediately after mapping the column.
          */}
          {D.withDedicationN === 0
            ? 'Nobody has a dedication value yet, so there is no shared cohort to allocate — this is a gap in the data rather than a finding about the design.'
            : 'Nobody here is shared: everybody with a value is either fully dedicated or not dedicated at all, so there is no cohort to allocate.'}
        </p>
      ) : (
        <>
          <div className="row" style={{ alignItems: 'center', gap: 12 }}>
            <span className="muted small">Measured in</span>
            <MetricToggle
              value={metric}
              onChange={onMetric}
              keys={offeredMetrics(state, ['hc', 'fte', 'cost'])}
              weight="raw"
              testIdPrefix="ded-metric"
              label=""
            />
            {state.groupingLevels.length > 0 && (
              <label>
                Broken down by
                <select
                  data-testid="ded-level"
                  value={levelKey ?? ''}
                  onChange={(e) => onLevelKey(e.target.value || null)}
                >
                  {state.groupingLevels.map((l) => (
                    <option key={l.key} value={l.key}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          <div
            className="stat-grid"
            data-testid="dedication-tiles"
            data-shared={D.sharedN}
            data-remain={D.toRemainN}
            data-carve={D.toCarveOutN}
            data-nlr={D.toNoLongerN}
          >
            <StatTile
              label="◐ Shared people"
              valueTestId="ded-shared"
              value={fmt(D.sharedN)}
              sub={D.anyWeight ? `${METRICS[metric].label}: ${format(D.sharedWeight)}` : 'no weight recorded'}
            />
            <StatTile
              label="→ Remain"
              valueTestId="ded-to-remain"
              value={pct(D.toRemainN, D.sharedN)}
              sub={`${fmt(D.toRemainN)} ${D.toRemainN === 1 ? 'person' : 'people'}`}
            />
            <StatTile
              label="→ Carve-out"
              valueTestId="ded-to-carve"
              value={pct(D.toCarveOutN, D.sharedN)}
              sub={`${fmt(D.toCarveOutN)} ${D.toCarveOutN === 1 ? 'person' : 'people'}`}
            />
            {/*
              The third destination, drawn whether or not anybody is in it. The app's two tiles divided
              by a total that included this one, so their percentages silently failed to sum.
            */}
            <StatTile
              label="→ No longer required"
              valueTestId="ded-to-nlr"
              value={pct(D.toNoLongerN, D.sharedN)}
              sub={`${fmt(D.toNoLongerN)} ${D.toNoLongerN === 1 ? 'person' : 'people'}`}
              danger={D.toNoLongerN > 0}
            />
          </div>
          <p className="muted small">
            Tiles are people, so they sum to 100% of the shared cohort. The table below measures in{' '}
            {METRICS[metric].label.toLowerCase()} and gives both denominators their own columns.
            Allocation uses this scenario&apos;s bucket assignments; the grouping is the imported
            hierarchy, which a scenario move does not change.
          </p>

          {levelKey === null ? (
            <p className="muted" data-testid="dedication-no-level">
              This project has no grouping level to break the cohort down by.
            </p>
          ) : (
            <table className="data-table" data-testid="dedication-table">
              <thead>
                <tr>
                  <th>{state.groupingLevels.find((l) => l.key === levelKey)?.label ?? levelKey}</th>
                  <th className="num">Shared ({METRICS[metric].label})</th>
                  {/* Named for their denominators, because the app's one pair depended on a button. */}
                  <th className="num">→ Remain (of shared)</th>
                  <th className="num">→ Carve-out (of shared)</th>
                  <th className="num">→ No longer req.</th>
                  <th className="num">Shared, of the group</th>
                  <th style={{ width: 150 }}>Allocation</th>
                </tr>
              </thead>
              <tbody>
                {D.groups.map((g, i) => (
                  <tr
                    key={g.key}
                    data-testid={`ded-row-${i}`}
                    data-key={g.key}
                    data-shared-n={g.sharedN}
                    data-shared={g.sharedWeight}
                    data-remain={g.toRemain}
                    data-carve={g.toCarveOut}
                    data-nlr={g.toNoLonger}
                    data-group={g.groupWeight}
                    data-nodata={g.noDataN}
                  >
                    <td>
                      {g.key}
                      {/*
                        Per-group coverage. Under a share-of-the-group reading, an unmeasured group looks
                        like a cleanly unshared one — 95% of the bar empty — and is de-prioritised
                        precisely because nobody has looked at it yet.
                      */}
                      {g.noDataN > 0 && (
                        <span className="muted small">
                          {' '}
                          — {fmt(g.noDataN)} of {fmt(g.groupN)} not yet measured
                        </span>
                      )}
                    </td>
                    <td className="num">
                      {/* A dash, not a zero: the weight is unmeasured, and the count is in the label. */}
                      {D.anyWeight ? format(g.sharedWeight) : '—'}
                    </td>
                    <td className="num">{pct(g.toRemain, g.sharedWeight)}</td>
                    <td className="num">{pct(g.toCarveOut, g.sharedWeight)}</td>
                    <td className="num">{pct(g.toNoLonger, g.sharedWeight)}</td>
                    <td className="num">{pct(g.sharedWeight, g.groupWeight)}</td>
                    <td>
                      <AllocationBar group={g} colors={colors} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}

/**
 * The three destinations as one bar, always over the shared cohort.
 *
 * Full width by construction, so the bar means "how this group's shared people divide" and never "how
 * much of this group is shared" — which the app's version meant only when a toggle elsewhere said so,
 * with a tooltip that named headcount whichever metric was chosen.
 */
function AllocationBar({
  group,
  colors,
}: {
  group: { sharedWeight: number; toRemain: number; toCarveOut: number; toNoLonger: number };
  colors: NodeDisplay['colors']['status'];
}): JSX.Element | null {
  const denom = group.sharedWeight;
  if (!denom) return null;
  const seg = (value: number, colour: string, label: string): JSX.Element | null =>
    value > 0 ? (
      <span
        key={label}
        style={{ display: 'block', height: '100%', width: `${((value / denom) * 100).toFixed(2)}%`, background: `#${colour}` }}
        title={`${label}: ${((value / denom) * 100).toFixed(0)}% of this group's shared cohort`}
      />
    ) : null;
  return (
    <div className="alloc-bar">
      {seg(group.toRemain, colors.Remain, 'Remain')}
      {seg(group.toCarveOut, colors['Carve-out'], 'Carve-out')}
      {seg(group.toNoLonger, colors['No longer required'], 'No longer required')}
    </div>
  );
}
